import React from 'react';
import ContentIconAtom from '../../icon-atoms/content-icon-atoms';
import styled from '@emotion/styled/macro';
import vars from '@variables';
import { Link, navigate } from 'gatsby';
import { GTM_ID } from '../../../../../common/constants/gtmIds';
import { ReverseProps, NavbarIconLinkProps } from './navbar-icon-link.types';
import { shouldForwardProp } from '@utils/utils';
import { WrapperProps } from '../navbar-atoms-common.types';

const sharedLinkStyles = hasBackground => {
  return `
    display: flex;
    text-decoration: none;
    padding: ${vars.spacingXs};
    padding-right: ${vars.spacingM};
    margin-left: -${vars.spacingXs};
    font-size: 1.6rem;
    align-items: center;
    width: fit-content;
    border-radius: 8px;
    
    &:hover {
      background-color: ${hasBackground ? '#fff' : '#f9fafb'};
    }

    &:last-of-type {
      margin-bottom: ${vars.spacingS};
    }
  
  `;
};

const StyledLink = styled(Link, {
  shouldForwardProp: prop => shouldForwardProp(['hasBackground'], prop),
})<WrapperProps>(({ hasBackground }) => {
  return sharedLinkStyles(hasBackground);
});

const StyledExternalLink = styled('a', {
  shouldForwardProp: prop => shouldForwardProp(['hasBackground'], prop),
})<WrapperProps>(({ hasBackground }) => sharedLinkStyles(hasBackground));

const IconWrapper = styled.span<ReverseProps>`
  display: flex;
  margin: ${({ reverse }) => (reverse ? '0' : `0 ${vars.spacingXs} 0 0`)};
  order: ${({ reverse }) => (reverse ? '1' : '0')};
`;

const Title = styled.p<ReverseProps>`
  font-weight: ${vars.fontWeightMedium};
  margin: ${({ reverse }) => (reverse ? `0 0 0 ${vars.spacingXs}` : '0')};
`;

const Wrapper: React.FC<WrapperProps> = ({
  isExternal,
  children,
  gtmId,
  hasBackground,
  link,
}) => {
  const handleClick = (e, link) => {
    e.preventDefault();
    const currentUrl = window.location.pathname;
    const sanitisedLink = new URL(`${window.location.origin}${link}`);

    if (sanitisedLink.pathname.includes(currentUrl)) {
      window.location.href = link;
    } else {
      navigate(link);
    }
  };

  if (isExternal) {
    return (
      <StyledExternalLink
        href={link}
        data-gtm-id={gtmId}
        hasBackground={hasBackground}
        target="_blank"
        rel="noreferrer noopener"
      >
        {children}
      </StyledExternalLink>
    );
  }

  return (
    <StyledLink
      onClick={e => handleClick(e, link)}
      to={link || ''}
      data-gtm-id={gtmId}
      hasBackground={hasBackground}
    >
      {children}
    </StyledLink>
  );
};

export const NavbarIconLink: React.FC<NavbarIconLinkProps> = ({
  item,
  columnNumber,
  hasBackground,
}) => {
  const reverseOrder = item.iconPosition === 'right';
  return (
    <Wrapper
      link={item.linkPath}
      gtmId={`${GTM_ID.FLYOVER_LINK_COLUMN}${columnNumber}`}
      hasBackground={hasBackground}
      isExternal={item.isExternalLink}
    >
      <IconWrapper reverse={reverseOrder}>
        <ContentIconAtom backgroundFill="transparent" contentType={item.icon} />
      </IconWrapper>
      <Title reverse={reverseOrder}>{item.title}</Title>
    </Wrapper>
  );
};
